import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  MoreVertical,
  Edit,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Image as ImageIcon,
  Calendar,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { SocialPost, SocialPlatform, SocialPostStatus, PLATFORM_INFO } from '@/types/social';
import { cn } from '@/lib/core/utils';
import { toast } from 'sonner';

interface PostsListProps {
  posts: SocialPost[];
  isLoading: boolean;
  onEdit?: (post: SocialPost) => void;
  onDelete?: (postId: string) => Promise<void>;
  onPublishNow?: (postId: string) => Promise<void>;
}

export const PostsList: React.FC<PostsListProps> = ({
  posts,
  isLoading,
  onEdit,
  onDelete,
  onPublishNow
}) => {
  const [selectedTab, setSelectedTab] = useState<'all' | 'scheduled' | 'published' | 'draft'>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [postToDelete, setPostToDelete] = useState<SocialPost | null>(null);

  const filteredPosts = posts.filter(post => {
    if (selectedTab === 'all') return true;
    if (selectedTab === 'scheduled') return post.status === 'scheduled';
    if (selectedTab === 'published') return post.status === 'published';
    if (selectedTab === 'draft') return post.status === 'draft';
    return true;
  });

  const scheduledCount = posts.filter(p => p.status === 'scheduled').length;
  const publishedCount = posts.filter(p => p.status === 'published').length;
  const draftCount = posts.filter(p => p.status === 'draft').length;

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  const getStatusBadge = (status: SocialPostStatus) => {
    switch (status) {
      case 'published':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Published
          </Badge>
        );
      case 'scheduled':
        return (
          <Badge>
            <Clock className="h-3 w-3 mr-1" />
            Scheduled
          </Badge>
        );
      case 'publishing':
        return (
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Publishing
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case 'draft':
        return (
          <Badge variant="outline">
            Draft
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Cancelled
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleDelete = async () => {
    if (!postToDelete || !onDelete) return;

    setDeletingId(postToDelete.id);
    try {
      await onDelete(postToDelete.id);
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeletingId(null);
      setPostToDelete(null);
    }
  };

  const handlePublishNow = async (post: SocialPost) => {
    if (!onPublishNow) return;

    setPublishingId(post.id);
    try {
      await onPublishNow(post.id);
      toast.success('Post published!');
    } catch (error) {
      toast.error('Failed to publish post');
    } finally {
      setPublishingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="flex gap-4">
                <div className="h-20 w-20 bg-muted rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 bg-muted rounded" />
                  <div className="h-3 w-1/2 bg-muted rounded" />
                  <div className="h-3 w-1/4 bg-muted rounded" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Tabs */}
      <Tabs value={selectedTab} onValueChange={(v) => setSelectedTab(v as any)}>
        <TabsList>
          <TabsTrigger value="all">
            All ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="scheduled">
            Scheduled ({scheduledCount})
          </TabsTrigger>
          <TabsTrigger value="published">
            Published ({publishedCount})
          </TabsTrigger>
          <TabsTrigger value="draft">
            Drafts ({draftCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Posts List */}
      {filteredPosts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-semibold">No posts yet</h3>
            <p className="text-sm text-muted-foreground">
              {selectedTab === 'scheduled'
                ? 'You have no scheduled posts.'
                : selectedTab === 'published'
                ? 'You have no published posts.'
                : selectedTab === 'draft'
                ? 'You have no draft posts.'
                : 'Create your first post to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPosts.map((post) => (
            <Card key={post.id}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Media Thumbnail */}
                  <div className="h-20 w-20 rounded-lg bg-muted flex-shrink-0 overflow-hidden">
                    {post.media_urls && post.media_urls.length > 0 ? (
                      <img
                        src={post.media_urls[0]}
                        alt="Post media"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm line-clamp-2">{post.content}</p>
                      </div>
                      {getStatusBadge(post.status)}
                    </div>

                    {/* Platforms */}
                    <div className="flex items-center gap-2 mt-2">
                      {post.target_platforms.map((platform) => {
                        const Icon = getPlatformIcon(platform);
                        const info = PLATFORM_INFO[platform];
                        return (
                          <div
                            key={platform}
                            className="rounded-full p-1"
                            style={{ backgroundColor: info.bgColor }}
                            title={info.name}
                          >
                            <Icon
                              className="h-3 w-3"
                              style={{ color: info.color }}
                            />
                          </div>
                        );
                      })}

                      <span className="text-xs text-muted-foreground ml-auto">
                        {post.status === 'scheduled' && post.scheduled_for ? (
                          <>
                            <Clock className="h-3 w-3 inline mr-1" />
                            {format(new Date(post.scheduled_for), 'PPP')} at{' '}
                            {format(new Date(post.scheduled_for), 'p')}
                          </>
                        ) : post.published_at ? (
                          <>Published {formatDistanceToNow(new Date(post.published_at), { addSuffix: true })}</>
                        ) : (
                          <>Created {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</>
                        )}
                      </span>
                    </div>

                    {/* Error message */}
                    {post.status === 'failed' && post.error_message && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-600">
                        {post.error_message}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {(post.status === 'draft' || post.status === 'scheduled') && onEdit && (
                          <DropdownMenuItem onClick={() => onEdit(post)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {(post.status === 'draft' || post.status === 'scheduled') && onPublishNow && (
                          <DropdownMenuItem
                            onClick={() => handlePublishNow(post)}
                            disabled={publishingId === post.id}
                          >
                            {publishingId === post.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4 mr-2" />
                            )}
                            Publish Now
                          </DropdownMenuItem>
                        )}
                        {post.status === 'published' && post.publish_results && (
                          <>
                            {Object.entries(post.publish_results).map(([platform, result]) => {
                              if (result?.url) {
                                return (
                                  <DropdownMenuItem key={platform} asChild>
                                    <a
                                      href={result.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <ExternalLink className="h-4 w-4 mr-2" />
                                      View on {PLATFORM_INFO[platform as SocialPlatform].name}
                                    </a>
                                  </DropdownMenuItem>
                                );
                              }
                              return null;
                            })}
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600"
                          onClick={() => setPostToDelete(post)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!postToDelete} onOpenChange={() => setPostToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this post? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingId === postToDelete?.id}
            >
              {deletingId === postToDelete?.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PostsList;
